/**
 * Off days — set by the direct manager (own team), HR or super admin (owner, 2026-11-04).
 * Backend: services/off_days.py, routes /api/attendance/off-days*.
 *
 *  - Standing off days: carried over to every pay period until changed. "Set for the team" writes the
 *    same days for everyone; editing one person makes an individual exception that a team action keeps.
 *  - Changes for one month: click a day to make it off or worked for this period only.
 *  - Monthly review: from the 20th the manager confirms the coming period; nothing blocks without it.
 *  - HR sees every manager: team size, how many have nothing set, confirmed or carried over.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, Lock, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import ReasonDialog from './ReasonDialog';
import { api, errorText } from './shared';

const NAMES = { en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  ar: ['الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت', 'الأحد'] };
const SHORT_AR = ['ن', 'ث', 'ر', 'خ', 'ج', 'س', 'ح'];
const PUBLISHED = ['hr_approved', 'employee_confirmed', 'disputed', 'final', 'released'];
const LOCKED = ['final', 'released'];
const FRI_SAT = ['work', 'work', 'work', 'work', 'off', 'off', 'work'];
const weekday = (iso) => (new Date(`${iso}T00:00:00Z`).getUTCDay() + 6) % 7;   // Monday = 0

function WeekPicker({ value, onChange, isRTL, testid }) {
  return (
    <div className="flex flex-wrap gap-1" data-testid={testid}>
      {NAMES.en.map((_, i) => (
        <button key={i} type="button" onClick={() => onChange(value.map((k, j) => (j === i ? (k === 'off' ? 'work' : 'off') : k)))}
          className={`px-2 py-1 rounded border text-xs ${value[i] === 'off' ? 'bg-yellow-100 border-yellow-300 text-yellow-900 font-semibold' : 'bg-white text-slate-600'}`}>
          {NAMES[isRTL ? 'ar' : 'en'][i]}{value[i] === 'off' ? (isRTL ? ' · راحة' : ' · off') : ''}
        </button>
      ))}
    </div>
  );
}

function Managers({ token, isRTL, period, onOpen }) {
  const t = (en, ar) => (isRTL ? ar : en);
  const [data, setData] = useState(null);
  useEffect(() => {
    api(token).get('/off-days/managers', period ? { period } : undefined).then(setData).catch((e) => toast.error(errorText(e)));
  }, [token, period]);
  if (!data) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
  return (
    <Card data-testid="offdays-managers">
      <CardHeader><CardTitle className="text-base">
        {t(`Managers — pay period ${data.period.start} – ${data.period.end}`, `المديرون — فترة الراتب ${data.period.start} – ${data.period.end}`)}
        <span className="ms-2 text-sm font-normal text-slate-500">
          {t(`${data.totals.nothing_set} of ${data.totals.active} active employees have nothing set`,
            `${data.totals.nothing_set} من ${data.totals.active} موظف نشط بلا أيام راحة محددة`)}
        </span>
      </CardTitle></CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 border-b">
            <tr>
              <th className="text-start py-1">{t('Manager', 'المدير')}</th>
              <th>{t('Team', 'الفريق')}</th>
              <th>{t('Standing days set', 'أيام ثابتة محددة')}</th>
              <th>{t('Changed this month', 'معدّل هذا الشهر')}</th>
              <th>{t('Nothing set', 'غير محدد')}</th>
              <th className="text-start">{t('This period', 'هذه الفترة')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.managers.map((m) => (
              <tr key={m.manager_employee_id} className="border-b" data-testid={`offdays-manager-${m.manager_employee_id}`}>
                <td className="py-1.5">
                  {m.manager_employee_id === 'none' ? <i>{t('No direct manager on the profile', 'لا يوجد مدير مباشر في الملف')}</i>
                    : ((isRTL ? m.name_ar : m.name) || m.name || <span className="text-slate-400">{t('— missing in profile', '— غير موجود في الملف')}</span>)}
                </td>
                <td className="text-center">{m.team}</td>
                <td className="text-center">{m.standing_set}</td>
                <td className="text-center">{m.adjusted}</td>
                <td className={`text-center ${m.nothing_set ? 'text-amber-700 font-semibold' : ''}`}
                  title={t(`office: ${m.nothing_set_office} (company default) · production/drivers: ${m.nothing_set_production} (needs review)`,
                    `مكتبي: ${m.nothing_set_office} (الافتراضي للشركة) · إنتاج/سائقون: ${m.nothing_set_production} (يحتاج مراجعة)`)}>
                  {m.nothing_set}
                </td>
                <td>
                  {m.manager_employee_id === 'none' ? '—' : m.review === 'confirmed'
                    ? <span className="text-emerald-700">{t(`Confirmed by ${m.confirmed_by_name || '—'}`, `أكده ${m.confirmed_by_name || '—'}`)}{m.on_behalf ? t(' (HR, on behalf)', ' (الموارد البشرية نيابةً)') : ''}</span>
                    : <span className="text-slate-500">{t('Carried over (not confirmed)', 'مستمرة (لم تؤكَّد)')}</span>}
                </td>
                <td className="text-end"><Button size="sm" variant="outline" onClick={() => onOpen(m.manager_employee_id)}>{t('Open', 'فتح')}</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

export default function OffDaysTab({ token, isRTL, access, period, managerId, onSelect, onConfirmed }) {
  const t = (en, ar) => (isRTL ? ar : en);
  const hr = !!access?.hr_scope;
  const [grid, setGrid] = useState(null);
  const [pending, setPending] = useState({});           // {employee_id: {day: 'off' | 'work' | null}}
  const [teamDays, setTeamDays] = useState(FRI_SAT);
  const [includeExceptions, setIncludeExceptions] = useState(false);
  const [dialog, setDialog] = useState(null);            // {kind: 'team' | 'adjust' | 'standing' | 'confirm', ...}
  const [result, setResult] = useState(null);
  const showTeam = !hr || !!managerId;

  const load = useCallback(() => {
    if (!showTeam) { setGrid(null); return; }
    const params = {};
    if (period) params.period = period;
    if (hr && managerId) params.manager_employee_id = managerId;
    api(token).get('/off-days', params).then((g) => { setGrid(g); setPending({}); }).catch((e) => toast.error(errorText(e)));
  }, [token, period, managerId, hr, showTeam]);
  useEffect(() => { load(); }, [load]);

  const changes = useMemo(() => Object.values(pending).reduce((n, d) => n + Object.keys(d).length, 0), [pending]);
  const key = grid?.period?.key || period;

  const editable = (row) => (hr ? !LOCKED.includes(row.timesheet_status) : !PUBLISHED.includes(row.timesheet_status));

  const toggle = (row, day) => {
    if (!editable(row)) return;
    const plan = row.plan[day];
    const mine = pending[row.employee_id] || {};
    const now = day in mine ? (mine[day] === null ? plan.base : mine[day]) : plan.kind;
    const next = now === 'off' ? 'work' : 'off';
    const stored = plan.layer === 'adjustment' ? plan.kind : null;   // what the server holds for this day
    const want = next === plan.base ? null : next;                    // back to the standing day: no adjustment
    const copy = { ...mine };
    if (want === stored) delete copy[day]; else copy[day] = want;
    setPending({ ...pending, [row.employee_id]: copy });
  };

  const run = async (fn, ok) => {
    try { const r = await fn(); toast.success(ok); setDialog(null); load(); return r; }
    catch (e) { toast.error(errorText(e)); return null; }
  };

  const applyTeam = (reason) => run(async () => {
    const r = await api(token).post('/off-days/team', {
      period: key, days: teamDays.map((k) => ({ kind: k })), reason, include_exceptions: includeExceptions,
      ...(hr ? { manager_employee_id: managerId === 'none' ? undefined : managerId,
        employee_ids: managerId === 'none' ? grid.rows.map((r2) => r2.employee_id) : undefined } : {}),
    });
    setResult(r);
    return r;
  }, t('Standing off days saved', 'تم حفظ أيام الراحة الثابتة'));

  const saveAdjustments = (reason) => run(
    () => api(token).put('/off-days/adjustments', { period: key, changes: pending, reason }),
    t('Changes for this month saved', 'تم حفظ تعديلات هذا الشهر'));

  const saveStanding = (reason) => run(
    () => api(token).put(`/work-patterns/${dialog.row.employee_id}`, {
      effective_from: grid.period.start, reason,
      // a half day stays a half day unless it is made an off day here
      days: dialog.days.map((k, i) => {
        const was = dialog.row.standing?.days?.[i];
        return k === 'work' && was?.kind === 'half' ? was : { kind: k };
      }) }),
    t('Saved for this employee', 'تم الحفظ لهذا الموظف'));

  const confirm = (note) => run(async () => {
    const r = await api(token).post('/off-days/confirm', { period: key, note: note || undefined,
      ...(hr ? { manager_employee_id: managerId } : {}) });
    if (onConfirmed) onConfirmed();
    return r;
  }, t('Off days confirmed', 'تم تأكيد أيام الراحة'));

  const nameOf = (r) => (isRTL ? r.name_ar || r.name : r.name) || '—';
  const by = (name, role) => name || (role === 'hr' ? t('HR', 'الموارد البشرية') : '—');

  return (
    <div className="space-y-4 mt-4" data-testid="offdays-tab">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-slate-600">{t('Pay period ending in', 'فترة الراتب المنتهية في')}</span>
        <Input type="month" className="w-44" value={key || ''} onChange={(e) => onSelect(e.target.value, managerId)} data-testid="offdays-period" />
        {hr && managerId && <Button size="sm" variant="outline" onClick={() => onSelect(key, null)}><Users className="h-4 w-4 me-1" />{t('All managers', 'كل المديرين')}</Button>}
        {grid && <span className="text-xs text-slate-500">
          {t(`Review for ${grid.review.period}: from ${grid.review.opens} to ${grid.review.closes}`,
            `مراجعة فترة ${grid.review.period}: من ${grid.review.opens} إلى ${grid.review.closes}`)}
        </span>}
      </div>

      {hr && !managerId && <Managers token={token} isRTL={isRTL} period={period} onOpen={(m) => onSelect(key, m)} />}

      {showTeam && !grid && <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
      {showTeam && grid && (
        <>
          <Card>
            <CardHeader><CardTitle className="text-base flex flex-wrap items-center gap-2">
              {t(`Off days — ${grid.period.start} – ${grid.period.end}`, `أيام الراحة — ${grid.period.start} – ${grid.period.end}`)}
              {grid.manager_employee_id !== 'none' && (grid.confirmation
                ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="offdays-confirmed">
                  <CheckCircle2 className="h-3 w-3 me-1" />{t(`Confirmed ${String(grid.confirmation.confirmed_at).slice(0, 16).replace('T', ' ')}`, `مؤكدة ${String(grid.confirmation.confirmed_at).slice(0, 16).replace('T', ' ')}`)}
                </Badge>
                : <Badge variant="outline" className="text-slate-500">{t('Not confirmed: the standing days carry over', 'لم تؤكَّد: تستمر الأيام الثابتة')}</Badge>)}
              <span className="ms-auto flex gap-2">
                {changes > 0 && <Button size="sm" onClick={() => setDialog({ kind: 'adjust' })} data-testid="offdays-save">
                  {t(`Save changes for this month (${changes})`, `حفظ تعديلات هذا الشهر (${changes})`)}</Button>}
                {grid.manager_employee_id && grid.manager_employee_id !== 'none' && (
                  <Button size="sm" variant="outline" disabled={changes > 0} onClick={() => setDialog({ kind: 'confirm' })} data-testid="offdays-confirm">
                    {t('Confirm off days for this period', 'تأكيد أيام الراحة لهذه الفترة')}</Button>)}
              </span>
            </CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="rounded-md border p-3 space-y-2 bg-slate-50" data-testid="offdays-team-form">
                <div className="text-sm font-medium">{t(`Standing off days for the team, from ${grid.period.start} on`, `أيام راحة ثابتة للفريق ابتداءً من ${grid.period.start}`)}</div>
                <WeekPicker value={teamDays} onChange={setTeamDays} isRTL={isRTL} testid="offdays-team-days" />
                <label className="flex items-center gap-2 text-xs text-slate-600">
                  <input type="checkbox" checked={includeExceptions} onChange={(e) => setIncludeExceptions(e.target.checked)} />
                  {t('Also replace individual exceptions', 'استبدال الاستثناءات الفردية أيضاً')}
                </label>
                <Button size="sm" variant="outline" disabled={!grid.rows.length} onClick={() => setDialog({ kind: 'team' })} data-testid="offdays-team-apply">
                  {t(`Set for ${grid.rows.length} employee(s)`, `تطبيق على ${grid.rows.length} موظف`)}</Button>
                {result && (
                  <div className="text-xs text-slate-600" data-testid="offdays-team-result">
                    {t(`Saved for ${result.applied.length}.`, `حُفظت لـ ${result.applied.length}.`)}
                    {result.kept_exceptions.length > 0 && t(` ${result.kept_exceptions.length} individual exception(s) kept.`, ` أُبقي ${result.kept_exceptions.length} استثناء فردي.`)}
                    {result.refused.length > 0 && <span className="text-rose-600">{t(` Refused: ${result.refused.map((x) => x.error).join('; ')}`, ` مرفوض: ${result.refused.map((x) => x.error).join('؛ ')}`)}</span>}
                  </div>
                )}
              </div>

              <div className="text-xs text-slate-500 flex flex-wrap gap-3">
                <span><span className="inline-block w-3 h-3 align-middle bg-yellow-100 border border-yellow-300 me-1" />{t('Off (standing)', 'راحة (ثابتة)')}</span>
                <span><span className="inline-block w-3 h-3 align-middle bg-yellow-200 border-2 border-orange-500 me-1" />{t('Changed for this month', 'معدّل لهذا الشهر')}</span>
                <span><span className="inline-block w-3 h-3 align-middle bg-amber-50 border border-amber-300 border-dashed me-1" />{t('Company default, not set by manager', 'الافتراضي للشركة، لم يحدده المدير')}</span>
                <span><span className="inline-block w-3 h-3 align-middle bg-rose-50 border border-rose-200 me-1" />{t('Off days not set', 'أيام الراحة غير محددة')}</span>
                <span><span className="inline-block w-3 h-3 align-middle bg-sky-100 border border-sky-400 me-1" />{t('Unsaved', 'غير محفوظ')}</span>
                <span>{t('Click a day to change it for this month only.', 'انقر يوماً لتغييره لهذا الشهر فقط.')}</span>
              </div>

              <div className="overflow-x-auto">
                <table className="text-xs border-collapse" data-testid="offdays-grid">
                  <thead>
                    <tr>
                      <th className="text-start p-1 min-w-[180px] sticky start-0 bg-white">{t('Employee', 'الموظف')}</th>
                      <th className="text-start p-1 min-w-[220px]">{t('Standing off days', 'أيام الراحة الثابتة')}</th>
                      {grid.days.map((d) => (
                        <th key={d} className={`p-0.5 w-7 text-center font-normal ${[4, 5].includes(weekday(d)) ? 'text-slate-700' : 'text-slate-400'}`}>
                          <div>{isRTL ? SHORT_AR[weekday(d)] : NAMES.en[weekday(d)][0]}</div><div>{Number(d.slice(8))}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {grid.rows.map((r) => {
                      const can = editable(r);
                      const mine = pending[r.employee_id] || {};
                      return (
                        <tr key={r.employee_id} className="border-t" data-testid={`offdays-row-${r.employee_id}`}>
                          <td className="p-1 sticky start-0 bg-white">
                            <div className="font-medium">{nameOf(r)}</div>
                            <div className="text-slate-400">{r.employee_number || ''} · {{ two_shift: t('two-shift', 'فترتان'), driver: t('driver', 'سائق'), office: t('office', 'مكتبي') }[r.profile_kind]}
                              {!can && <Lock className="inline h-3 w-3 ms-1" title={t('approved: HR changes it', 'معتمد: تغيّره الموارد البشرية')} />}</div>
                          </td>
                          <td className="p-1">
                            {r.standing ? (
                              <div>
                                <div>{r.standing.describe[isRTL ? 'ar' : 'en']}
                                  {r.standing.scope === 'individual' && <Badge variant="outline" className="ms-1 text-[10px]">{t('individual', 'فردي')}</Badge>}</div>
                                <div className="text-slate-400">{t(`set by ${by(r.standing.set_by_name, r.standing.set_by_role)} · from ${r.standing.effective_from}`,
                                  `حددها ${by(r.standing.set_by_name, r.standing.set_by_role)} · من ${r.standing.effective_from}`)}</div>
                              </div>
                            ) : r.profile_kind === 'office'
                              ? <span className="text-amber-700">{t('company default, not set by manager', 'الافتراضي للشركة، لم يحدده المدير')}</span>
                              : <span className="text-rose-600">{t('not set — days without punches need review', 'غير محددة — الأيام بلا بصمات تحتاج مراجعة')}</span>}
                            {can && <button type="button" className="block text-brand-orange underline mt-0.5"
                              onClick={() => setDialog({ kind: 'standing', row: r, days: (r.standing?.days || FRI_SAT.map((k) => ({ kind: k }))).map((x) => (typeof x === 'string' ? x : x.kind === 'off' ? 'off' : 'work')) })}
                              data-testid={`offdays-edit-${r.employee_id}`}>{t('Change for this employee', 'تغيير لهذا الموظف')}</button>}
                          </td>
                          {grid.days.map((d) => {
                            const p = r.plan[d];
                            const changed = d in mine;
                            const kind = changed ? (mine[d] === null ? p.base : mine[d]) : p.kind;
                            let cls = kind === 'off' ? 'bg-yellow-100 border-yellow-300' : 'bg-white border-slate-200';
                            if (!changed && p.layer === 'adjustment') cls = kind === 'off' ? 'bg-yellow-200 border-orange-500 border-2' : 'bg-white border-orange-500 border-2';
                            if (!changed && p.layer === 'company_default') cls = kind === 'off' ? 'bg-amber-50 border-amber-300 border-dashed' : 'bg-white border-slate-200';
                            if (!changed && p.layer === 'not_set') cls = 'bg-rose-50 border-rose-200';
                            if (changed) cls = kind === 'off' ? 'bg-sky-100 border-sky-400 border-2' : 'bg-white border-sky-400 border-2';
                            return (
                              <td key={d} className="p-0.5">
                                <button type="button" disabled={!can} onClick={() => toggle(r, d)}
                                  title={p.layer === 'adjustment' ? t(`changed by ${p.by_name || '—'}: ${p.reason || ''}`, `عدّله ${p.by_name || '—'}: ${p.reason || ''}`) : ''}
                                  className={`w-6 h-6 rounded border text-[10px] ${cls} ${can ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'}`}
                                  data-testid={`offdays-cell-${r.employee_id}-${d}`}>
                                  {kind === 'off' ? (isRTL ? 'ر' : 'O') : kind === 'half' ? '½' : ''}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                    {!grid.rows.length && <tr><td colSpan={2 + grid.days.length} className="p-4 text-center text-slate-500">{t('No employees in this team.', 'لا يوجد موظفون في هذا الفريق.')}</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      <ReasonDialog open={dialog?.kind === 'team'} isRTL={isRTL} onCancel={() => setDialog(null)} onConfirm={applyTeam}
        title={t('Standing off days for the team', 'أيام راحة ثابتة للفريق')}
        description={t(`${teamDays.map((k, i) => (k === 'off' ? NAMES.en[i] : null)).filter(Boolean).join(', ') || 'No off days'} — from ${grid?.period?.start} on, carried over every month until changed.`,
          `${teamDays.map((k, i) => (k === 'off' ? NAMES.ar[i] : null)).filter(Boolean).join('، ') || 'بلا أيام راحة'} — ابتداءً من ${grid?.period?.start}، وتستمر كل شهر حتى تُغيَّر.`)} />
      <ReasonDialog open={dialog?.kind === 'adjust'} isRTL={isRTL} onCancel={() => setDialog(null)} onConfirm={saveAdjustments}
        title={t('Changes for this month', 'تعديلات هذا الشهر')}
        description={t(`${changes} day(s) in ${key}, for this pay period only.`, `${changes} يوم في ${key}، لهذه الفترة فقط.`)} />
      <ReasonDialog open={dialog?.kind === 'standing'} isRTL={isRTL} onCancel={() => setDialog(null)} onConfirm={saveStanding}
        title={t('Standing off days for this employee', 'أيام راحة ثابتة لهذا الموظف')}
        description={dialog?.row ? t(`${nameOf(dialog.row)} — from ${grid?.period?.start} on. A team action keeps this individual exception.`,
          `${nameOf(dialog.row)} — ابتداءً من ${grid?.period?.start}. يبقى هذا الاستثناء الفردي عند تطبيق أيام الفريق.`) : ''}>
        {dialog?.kind === 'standing' && (
          <WeekPicker value={dialog.days} onChange={(days) => setDialog({ ...dialog, days })} isRTL={isRTL} testid="offdays-standing-days" />
        )}
      </ReasonDialog>
      <ReasonDialog open={dialog?.kind === 'confirm'} isRTL={isRTL} optional onCancel={() => setDialog(null)} onConfirm={confirm}
        title={t(`Confirm off days for ${key}`, `تأكيد أيام الراحة لفترة ${key}`)}
        description={t('A note is optional.', 'الملاحظة اختيارية.')} />
    </div>
  );
}
